import React from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import {
  CreditCard,
  Wallet,
  TrendingUp,
  AlertTriangle,
  Loader,
} from 'lucide-react';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
`;

const CardsGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  box-shadow: ${props => props.theme.shadows.sm};
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const StatValue = styled.span`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const SecondaryValue = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
`;

function BillingDashboard() {
  const { data, isLoading, error } = useQuery(
    'admin.billingOverview',
    () => adminAPI.getBillingOverview().then(res => res.data),
    {
      refetchInterval: 60000,
    }
  );

  const renderStatusBadge = status => {
    const config = {
      succeeded: { bg: '#d1fae5', color: '#047857', label: 'Успешно' },
      paid: { bg: '#d1fae5', color: '#047857', label: 'Оплачен' },
      completed: { bg: '#d1fae5', color: '#047857', label: 'Завершен' },
      pending: { bg: '#fef3c7', color: '#b45309', label: 'В ожидании' },
      processing: { bg: '#fef3c7', color: '#b45309', label: 'Обрабатывается' },
      failed: { bg: '#fee2e2', color: '#b91c1c', label: 'Ошибка' },
      canceled: { bg: '#f3f4f6', color: '#4b5563', label: 'Отменен' },
      refunded: { bg: '#f3f4f6', color: '#4b5563', label: 'Возврат' },
    }[status?.toLowerCase()] || { bg: '#e5e7eb', color: '#374151', label: status };

    return (
      <StatusBadge bg={config.bg} color={config.color}>
        {config.label}
      </StatusBadge>
    );
  };

  const formatNumber = value =>
    Number(value || 0).toLocaleString('ru-RU', {
      maximumFractionDigits: 2,
    });

  if (isLoading) {
    return (
      <EmptyState>
        <Loader size={20} /> Загрузка сводки...
      </EmptyState>
    );
  }

  if (error) {
    return (
      <EmptyState>
        <AlertTriangle size={20} /> Не удалось загрузить данные биллинга
      </EmptyState>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <CreditCard size={32} />
          Биллинг и платежи
        </Title>
        <Subtitle>Сводные показатели, заявки на пополнение и свежие транзакции</Subtitle>
      </Header>

      <CardsGrid>
        <StatCard>
          <StatLabel>Совокупный баланс пользователей</StatLabel>
          <StatValue>{formatNumber(data?.total_credits)} кред.</StatValue>
          <SecondaryValue>Хранится на счетах клиентов</SecondaryValue>
        </StatCard>

        <StatCard>
          <StatLabel>Выручка за текущий месяц</StatLabel>
          <StatValue>₽{formatNumber(data?.monthly_revenue)}</StatValue>
          <SecondaryValue>
            Успешные платежи: {formatNumber(data?.successful_payments)}
          </SecondaryValue>
        </StatCard>

        <StatCard>
          <StatLabel>Ожидают подтверждения</StatLabel>
          <StatValue>{formatNumber(data?.pending_payments)}</StatValue>
          <SecondaryValue>Платежи со статусом pending / processing</SecondaryValue>
        </StatCard>

        <StatCard>
          <StatLabel>Ошибки и возвраты</StatLabel>
          <StatValue>{formatNumber(data?.failed_payments)}</StatValue>
          <SecondaryValue>Проверить в логах платёжного провайдера</SecondaryValue>
        </StatCard>

        <StatCard>
          <StatLabel>Активные пользователи</StatLabel>
          <StatValue>{formatNumber(data?.total_users)}</StatValue>
          <SecondaryValue>Имеют аккаунт в системе</SecondaryValue>
        </StatCard>

        <StatCard>
          <StatLabel>Недавние операции</StatLabel>
          <StatValue>{formatNumber(data?.recent_payments?.length || 0)}</StatValue>
          <SecondaryValue>Последние 5 платежей</SecondaryValue>
        </StatCard>
      </CardsGrid>

      <Section>
        <SectionTitle>
          <TrendingUp size={20} />
          Последние платежи
        </SectionTitle>

        <TableContainer>
          {data?.recent_payments?.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>ID</Th>
                  <Th>Пользователь</Th>
                  <Th>Провайдер</Th>
                  <Th>Сумма</Th>
                  <Th>Статус</Th>
                  <Th>Дата</Th>
                </tr>
              </thead>
              <tbody>
                {data.recent_payments.map(payment => (
                  <tr key={payment.id}>
                    <Td>{payment.id}</Td>
                    <Td>
                      #{payment.user_internal_id}{' '}
                      {payment.username ? `(${payment.username})` : ''}
                      {payment.user_tg_id ? ` / TG ${payment.user_tg_id}` : ''}
                    </Td>
                    <Td>{payment.provider}</Td>
                    <Td>
                      ₽{formatNumber(payment.amount_rub)}{' '}
                      <SecondaryValue>{formatNumber(payment.credits)} кред.</SecondaryValue>
                    </Td>
                    <Td>{renderStatusBadge(payment.status)}</Td>
                    <Td>
                      {payment.created_at
                        ? new Date(payment.created_at).toLocaleString('ru-RU')
                        : '-'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyState>Платежей пока нет</EmptyState>
          )}
        </TableContainer>
      </Section>
    </Container>
  );
}

export default BillingDashboard;

